import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { MoneyInput } from '../components/MoneyInput';
import { Switch } from '../components/ui/switch';
import { format } from 'date-fns';
import { CalendarIcon, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function SaleFormPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [loading, setLoading] = useState(false);
  const [calStates, setCalStates] = useState({});
  const [form, setForm] = useState({
    submission_datetime: new Date().toISOString(),
    location_id: '',
    patient_first_name: '',
    patient_last_name: '',
    patient_email: '',
    patient_phone: '',
    initial_consult_date: format(new Date(), 'yyyy-MM-dd'),
    treatment_sold: '',
    total_sales_amount: '',
    total_collected_today: '',
    payment_method: '',
    financing_amount: '',
    other_funds_amount: '',
    anticipated_paid_in_full_date: '',
    treatment_start_date: '',
    referral_source: '',
    will_use_dental_insurance: false,
    closed_by: '',
    notes: ''
  });

  useEffect(() => {
    axios.get(`${API}/locations`).then(r => setLocations(r.data.filter(l => l.active))).catch(() => {});
    axios.get(`${API}/dropdowns`).then(r => setDropdowns(r.data)).catch(() => {});
  }, []);

  const setField = (name, val) => setForm(prev => ({ ...prev, [name]: val }));
  const setCal = (name, open) => setCalStates(prev => ({ ...prev, [name]: open }));

  const DateField = ({ label, fieldName, required }) => (
    <div className="space-y-1">
      <Label className="text-slate-700 text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Popover open={calStates[fieldName]} onOpenChange={o => setCal(fieldName, o)}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-left bg-white" data-testid={`sale-form-${fieldName}`}>
            <CalendarIcon size={14} className="mr-2 text-slate-400" />
            {form[fieldName] ? format(new Date(form[fieldName] + 'T00:00:00'), 'MMM d, yyyy') : `Select ${label}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white">
          <Calendar mode="single"
            selected={form[fieldName] ? new Date(form[fieldName] + 'T00:00:00') : undefined}
            onSelect={(d) => { if (d) { setField(fieldName, format(d, 'yyyy-MM-dd')); setCal(fieldName, false); } }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_id || !form.patient_first_name || !form.patient_last_name || !form.treatment_sold || !form.payment_method || !form.referral_source) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!form.total_sales_amount || parseFloat(form.total_sales_amount) <= 0) {
      toast.error('Total sales amount is required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/sales`, {
        ...form,
        total_sales_amount: parseFloat(form.total_sales_amount) || 0,
        total_collected_today: parseFloat(form.total_collected_today) || 0,
        financing_amount: parseFloat(form.financing_amount) || 0,
        other_funds_amount: parseFloat(form.other_funds_amount) || 0,
        anticipated_paid_in_full_date: form.anticipated_paid_in_full_date || null,
        treatment_start_date: form.treatment_start_date || null
      });
      toast.success('Sale recorded successfully!');
      navigate('/sales/records');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight">Record Individual Sale</h1>
        <p className="text-slate-500 text-sm mt-1">Capture detailed sale information for revenue and attribution tracking</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Submission Details</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Location <span className="text-red-500">*</span></Label>
                <Select value={form.location_id} onValueChange={v => setField('location_id', v)}>
                  <SelectTrigger className="bg-white" data-testid="sale-form-location">
                    <SelectValue placeholder="Select location..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DateField label="Initial Consult Date" fieldName="initial_consult_date" required />
            </div>
          </CardContent>
        </Card>
        {/* Patient */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Patient Information</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">First Name <span className="text-red-500">*</span></Label>
                <Input value={form.patient_first_name} onChange={e => setField('patient_first_name', e.target.value)}
                  className="bg-white border-slate-200" placeholder="John" data-testid="sale-form-patient-first" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Last Name <span className="text-red-500">*</span></Label>
                <Input value={form.patient_last_name} onChange={e => setField('patient_last_name', e.target.value)}
                  className="bg-white border-slate-200" placeholder="Smith" data-testid="sale-form-patient-last" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Email <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input value={form.patient_email} onChange={e => setField('patient_email', e.target.value)}
                  type="email" className="bg-white border-slate-200" placeholder="patient@email.com" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Phone <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input value={form.patient_phone} onChange={e => setField('patient_phone', e.target.value)}
                  type="tel" className="bg-white border-slate-200" placeholder="(555) 000-0000" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Treatment */}
        <Card className="bg-white shadow-sm border-l-4" style={{ borderLeftColor: 'hsl(191, 95%, 43%)' }}>
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Treatment & Sale</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Treatment Sold <span className="text-red-500">*</span></Label>
                <Select value={form.treatment_sold} onValueChange={v => setField('treatment_sold', v)}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select treatment..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(dropdowns.treatment_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Total Sales Amount <span className="text-red-500">*</span></Label>
                <MoneyInput value={form.total_sales_amount} onValueChange={v => setField('total_sales_amount', v)}
                  data-testid="sale-form-amount-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Total Collected Today</Label>
                <MoneyInput value={form.total_collected_today} onValueChange={v => setField('total_collected_today', v)} />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Payment Method <span className="text-red-500">*</span></Label>
                <Select value={form.payment_method} onValueChange={v => setField('payment_method', v)}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select method..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(dropdowns.payment_methods || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Financing Amount</Label>
                <MoneyInput value={form.financing_amount} onValueChange={v => setField('financing_amount', v)} />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Other Funds Amount</Label>
                <MoneyInput value={form.other_funds_amount} onValueChange={v => setField('other_funds_amount', v)} />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Dates */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Timeline</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DateField label="Anticipated Paid In Full Date" fieldName="anticipated_paid_in_full_date" />
              <DateField label="Treatment Start Date" fieldName="treatment_start_date" />
            </div>
          </CardContent>
        </Card>
        {/* Attribution */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Attribution</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Referral Source <span className="text-red-500">*</span></Label>
                <Select value={form.referral_source} onValueChange={v => setField('referral_source', v)}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select source..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(dropdowns.referral_sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Will Use Dental Insurance?</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Switch
                    checked={form.will_use_dental_insurance}
                    onCheckedChange={v => setField('will_use_dental_insurance', v)}
                  />
                  <span className="text-sm text-slate-600">{form.will_use_dental_insurance ? 'Yes' : 'No'}</span>
                </div>
              </div>
              {(dropdowns.sales_staff?.length > 0) && (
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">
                    Closed By <span className="text-slate-400 text-xs">(optional)</span>
                  </Label>
                  <Select value={form.closed_by || '__none__'} onValueChange={v => setField('closed_by', v === '__none__' ? '' : v)}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select staff member..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__none__">— Not specified —</SelectItem>
                      {(dropdowns.sales_staff || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {/* Notes */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Notes</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              placeholder="Financial qualification notes, case details..."
              className="bg-white border-slate-200 min-h-[80px]"
            />
          </CardContent>
        </Card>
        {/* Actions */}
        <div className="flex gap-3 pb-6">
          <Button type="submit" disabled={loading}
            className="gap-2 font-semibold active:scale-[0.98]"
            className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}
            data-testid="sale-form-submit-button"
          >
            <Save size={15} />
            {loading ? 'Saving...' : 'Record Sale'}
          </Button>
          <Button type="button" variant="outline" className="bg-white" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
